import { useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Paperclip } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth, usePrefs } from "@/lib/context";
import { fetcher, useCategories, useTicketTitle } from "@/lib/hooks";
import { PageHeader, StatusBadge, Empty } from "@/components/common";
import { TicketDialog } from "@/components/TicketDialog";
import { SlaBadge } from "@/components/FormBits";

const FILTERS = ["", "ouvert", "attribue", "en_cours", "resolu", "cloture"];

export function ProviderBanner() {
  const { user } = useAuth();
  const { t } = usePrefs();
  if (user.role !== "prestataire" || user.provider_status === "validated") return null;
  return (
    <div className={`mb-6 rounded-sm border p-4 text-sm ${user.provider_status === "rejected" ? "border-rose-200 bg-rose-50 text-rose-800" : "border-amber-200 bg-amber-50 text-amber-900"}`} data-testid="provider-status-banner">
      {user.provider_status === "rejected" ? t("providerRejected") : t("providerPending")} {!window.location.pathname.startsWith("/provider") && <Link to="/provider" className="font-semibold underline">{t("provider")}</Link>}
    </div>
  );
}

export default function Tickets() {
  const title = useTicketTitle();
  const { user } = useAuth();
  const { t, fdatetime } = usePrefs();
  const { label } = useCategories();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") || "";
  const { data = [], isLoading } = useQuery({ queryKey: ["tickets", status], queryFn: () => fetcher(`/tickets?kind=incident${status ? `&status=${status}` : ""}`), refetchInterval: 10000 });
  return (
    <div data-testid="tickets-page">
      <PageHeader eyebrow={t("ticketsEyebrow")} title={t("tickets")}>
        {user.role !== "prestataire" && <TicketDialog tenant={user.role === "locataire"} trigger={<Button className="rounded-sm bg-[#0F172A] hover:bg-[#B8860B]" data-testid="new-ticket-button"><Plus size={16} className="mr-1" />{t("newTicket")}</Button>} />}
      </PageHeader>
      <ProviderBanner />
      <div className="mb-5 flex flex-wrap gap-1.5">
        {FILTERS.map((s) => (
          <button key={s || "all"} onClick={() => setParams(s ? { status: s } : {})} data-testid={`ticket-filter-${s || "all"}`}
            className={`rounded-sm border px-3 py-1.5 text-xs font-semibold transition-colors ${status === s ? "border-[#0F172A] bg-[#0F172A] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-400"}`}>
            {s ? t(s) : t("all")}
          </button>
        ))}
      </div>
      {!isLoading && data.length === 0 && <Empty />}
      <div className="divide-y divide-slate-100 overflow-hidden rounded-sm border border-slate-200 bg-white">
        {data.map((tk) => (
          <Link key={tk.id} to={`/tickets/${tk.id}`} data-testid={`ticket-row-${tk.id}`} className="grid min-w-0 gap-2 px-5 py-4 transition-colors [&>span]:justify-self-start hover:bg-slate-50 md:grid-cols-[110px_1fr_160px_100px_110px] md:items-center">
            <span className="font-data text-xs text-slate-400">{tk.number}</span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2 font-semibold text-slate-900">{title(tk)}{tk.photos?.length > 0 && <Paperclip size={12} className="text-slate-400" />}<SlaBadge tk={tk} />{tk.kind === "preventif" && <span className="text-[10px] font-bold uppercase text-[#996515]">{t("preventif")}</span>}</div>
              <div className="truncate text-xs text-slate-500">{tk.property_name}{tk.unit_ref ? ` · ${tk.unit_ref}` : ""} · {label(tk.category)} · {fdatetime(tk.updated_at)}</div>
            </div>
            <span className="truncate text-xs text-slate-600">{tk.assigned_name || "—"}</span>
            <StatusBadge value={tk.urgency} />
            <StatusBadge value={tk.status} testId={`ticket-status-${tk.id}`} />
          </Link>
        ))}
      </div>
    </div>
  );
}
